import { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Button, TitleTextInput } from 'src/components/atoms';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import { casePageVm } from 'src/components/pages/Case/Case.slice';
import { SaveDocumentButtonStates } from 'src/types';
import {
  EditorHeader,
  EditorHeaderContainer,
} from './DocumentEditorHeader.styles';

interface Props {
  closeEditor: () => void;
  saveDocument: () => void;
  title: string;
  setTitle: (value: string) => void;
  documentHasChanged?: boolean;
  documentState: SaveDocumentButtonStates;
}

export const DocumentEditorHeader: FC<Props> = (props) => {
  const getButtonIcon = () => {
    if (props.documentState === SaveDocumentButtonStates.UNSAVED) {
      return 'uil uil-save';
    }
    if (props.documentState === SaveDocumentButtonStates.SAVED) {
      return 'uil uil-check';
    }
    return undefined;
  };

  const reduxCase = useSelector(casePageVm);

  const userHasReadOnlyAccess = useMemo(
    () =>
      reduxCase.permissions.canViewCaseWorkspaceTab &&
      !reduxCase.permissions.canEditCaseWorkspaceTab,
    [
      reduxCase.permissions.canEditCaseWorkspaceTab,
      reduxCase.permissions.canViewCaseWorkspaceTab,
    ],
  );

  return (
    <EditorHeaderContainer data-testid="case-document-editor-header">
      <EditorHeader>
        <TitleTextInput
          initialValue={props.title}
          placeholder={'Untitled document'}
          onChange={props.setTitle}
          updateTextOnChange
          data-testid="document-title-input"
          disabled={userHasReadOnlyAccess}
        />
        {!userHasReadOnlyAccess && props.documentHasChanged && (
          <Button
            variant="secondary"
            size="medium"
            onClick={props.closeEditor}
            data-testid="close-document-editor-button"
            width="150px"
          >
            Cancel
          </Button>
        )}
        {!userHasReadOnlyAccess && (
          <Button
            variant="primary"
            size="medium"
            data-testid="save-document-button"
            onClick={props.saveDocument}
            disabled={
              !props.documentHasChanged &&
              props.documentState === SaveDocumentButtonStates.UNSAVED
            }
            loading={props.documentState === SaveDocumentButtonStates.SAVING}
          >
            <>
              <StyledIconInButton
                size="medium"
                position="left"
                className={getButtonIcon()}
              />
              {props.documentState}
            </>
          </Button>
        )}
      </EditorHeader>
    </EditorHeaderContainer>
  );
};
